import { renderRichBlockHtml, stripRichText } from "../editor/richText.mjs";
import { isTiptapDocument } from "../editor/articleDocument.mjs";
import { articleToWechatPublishHtml } from "./wechatPublishHtml.mjs";

export function articleToMarkdown(article, options = {}) {
  const title = displayArticleTitle(article?.title);
  if (isTiptapDocument(article?.contentJson)) {
    const body = renderDocumentMarkdown(article.contentJson).trim();
    const documentMarkdown = [`# ${escapeMarkdownText(title)}`, body].filter(Boolean).join("\n\n");
    return appendMarkdownMetadata(documentMarkdown, options);
  }
  const lines = [`# ${title}`, "", article.summary, ""];

  for (const section of article.sections) {
    lines.push(`## ${section.heading}`, "");
    for (const paragraph of section.paragraphs) {
      lines.push(stripRichText(paragraph), "");
    }
    const images = article.imageSuggestions.filter((image) => image.placement.includes(section.heading.slice(0, 2)));
    for (const image of images) {
      lines.push(`![图片占位：${image.title}]()`, "");
    }
  }

  if (article.imageSuggestions.length > 0) {
    lines.push("## 配图建议", "");
    for (const image of article.imageSuggestions) {
      lines.push(`![图片占位：${image.title}]()`, "");
      lines.push(`- ${image.placement}：${image.title}。${image.description}`);
    }
    lines.push("");
  }

  return appendMarkdownMetadata(lines.join("\n").trimEnd(), options);
}

function appendMarkdownMetadata(markdown, options) {
  const lines = [markdown];
  if (options.unresolvedWarnings?.length) {
    lines.push("");
    lines.push("## 发布前待确认", "");
    for (const warning of options.unresolvedWarnings) lines.push(`- ${warning}`);
    lines.push("");
  }

  if (options.sources?.length) {
    lines.push("## 来源线索", "");
    for (const source of options.sources) lines.push(`- ${source}`);
    lines.push("");
  }

  return lines.join("\n").trimEnd();
}

function renderDocumentMarkdown(document) {
  return (document.content || [])
    .filter((node) => node.attrs?.role !== "title")
    .map((node) => renderMarkdownNode(node))
    .filter(Boolean)
    .join("\n\n");
}

function renderMarkdownNode(node, depth = 0) {
  const inline = () => (node.content || []).map(renderMarkdownInline).join("");
  if (node.type === "heading") return `${"#".repeat(node.attrs?.level || 2)} ${inline()}`;
  if (node.type === "paragraph") return inline();
  if (node.type === "blockquote") return (node.content || []).map((child) => renderMarkdownNode(child, depth)).join("\n").split("\n").map((line) => `> ${line}`).join("\n");
  if (node.type === "bulletList" || node.type === "orderedList") {
    return (node.content || []).map((item, index) => {
      const marker = node.type === "orderedList" ? `${(node.attrs?.start || 1) + index}.` : "-";
      const continuation = "  ".repeat(depth + 1);
      const body = renderMarkdownNode(item, depth + 1).replaceAll("\n", `\n${continuation}`);
      return `${"  ".repeat(depth)}${marker} ${body}`;
    }).join("\n");
  }
  if (node.type === "taskList") {
    return (node.content || []).map((item) => {
      const marker = item.attrs?.checked ? "- [x]" : "- [ ]";
      const children = (item.content || []).map((child) => ({
        type: child.type,
        value: renderMarkdownNode(child, depth + 1)
      }));
      const continuation = "  ".repeat(depth + 1);
      const first = (children.shift()?.value || "").replaceAll("\n", `\n${continuation}`);
      const rest = children.map(({ type, value }) => {
        const block = ["bulletList", "orderedList", "taskList"].includes(type)
          ? value
          : `${continuation}${value.replaceAll("\n", `\n${continuation}`)}`;
        return `${continuation}\n${block}`;
      });
      return `${"  ".repeat(depth)}${marker} ${first}${rest.length ? `\n${rest.join("\n")}` : ""}`;
    }).join("\n");
  }
  if (node.type === "listItem") return (node.content || []).map((child) => renderMarkdownNode(child, depth)).join("\n\n");
  if (node.type === "codeBlock") {
    const code = (node.content || []).map((child) => child.text || "").join("");
    const longestFence = Math.max(0, ...Array.from(code.matchAll(/`+/g), (match) => match[0].length));
    const fence = "`".repeat(Math.max(3, longestFence + 1));
    return `${fence}${node.attrs?.language || ""}\n${code}\n${fence}`;
  }
  if (node.type === "horizontalRule") return "---";
  if (node.type === "imagePlaceholder") return formatImagePlaceholderMarkdown(node.attrs || {});
  return inline();
}

function renderMarkdownInline(node) {
  if (node.type === "hardBreak") return "  \n";
  const marks = node.marks || [];
  const raw = node.text || (node.content || []).map(renderMarkdownInline).join("");
  const codeMark = marks.some((mark) => mark.type === "code");
  let value = codeMark ? renderInlineCode(raw) : escapeMarkdownText(raw);
  for (const mark of marks) {
    if (mark.type === "bold") value = `**${value}**`;
    else if (mark.type === "italic") value = `*${value}*`;
    else if (mark.type === "strike") value = `~~${value}~~`;
    else if (mark.type === "link") value = `[${value}](${escapeMarkdownUrl(mark.attrs?.href || "")})`;
    else if (mark.type === "highlight" && mark.attrs?.color == null) value = `==${value}==`;
  }
  return value;
}

function escapeMarkdownText(value) {
  return String(value).replace(/([\\`*_[\]{}<>#+\-.!|>])/g, "\\$1");
}

function escapeMarkdownUrl(value) {
  return encodeURI(String(value)).replaceAll("(", "%28").replaceAll(")", "%29");
}

function renderInlineCode(value) {
  const raw = String(value);
  const longestRun = Math.max(0, ...Array.from(raw.matchAll(/`+/g), (match) => match[0].length));
  const delimiter = "`".repeat(longestRun + 1);
  const padding = raw.startsWith("`") || raw.endsWith("`") ? " " : "";
  return `${delimiter}${padding}${raw}${padding}${delimiter}`;
}

export function articleToPlainText(article) {
  const lines = [displayArticleTitle(article?.title), "", article.summary, ""];
  for (const section of article.sections) {
    lines.push(section.heading, "");
    for (const paragraph of section.paragraphs) lines.push(stripRichText(paragraph), "");
  }
  return lines.join("\n").trim();
}

function displayArticleTitle(value) {
  return String(value || "").trim() || "未命名文章";
}

const IMAGE_PLACEHOLDER_PATTERN = /^!\[图片占位：(.+?)\]\(\)\s*(.+)$/;

export function parseImagePlaceholderParagraph(paragraph) {
  const match = String(paragraph || "").match(IMAGE_PLACEHOLDER_PATTERN);
  if (!match) return null;

  const title = match[1].trim();
  const rest = match[2].trim();
  const colonIndex = rest.indexOf("：");
  return {
    title,
    placement: colonIndex === -1 ? rest : rest.slice(0, colonIndex).trim(),
    description: colonIndex === -1 ? "" : rest.slice(colonIndex + 1).trim(),
    raw: String(paragraph)
  };
}

export function renderImagePlaceholderHtml(placeholder) {
  const raw = placeholder.raw || formatImagePlaceholderMarkdown(placeholder);
  return `<figure class="zf-image-placeholder" data-placeholder="${escapeHtml(raw)}">
  <div class="zf-image-placeholder__frame">
    <span class="zf-image-placeholder__badge">图片占位</span>
    <strong class="zf-image-placeholder__title">${escapeHtml(placeholder.title)}</strong>
    <span class="zf-image-placeholder__meta">${escapeHtml(placeholder.placement)}${placeholder.description ? `：${escapeHtml(placeholder.description)}` : ""}</span>
  </div>
</figure>`;
}

export function renderArticleParagraphHtml(paragraph) {
  return renderRichBlockHtml(paragraph);
}

export function formatImagePlaceholderMarkdown({ title, placement, description }) {
  const safeTitle = title || "配图建议";
  const safePlacement = placement || "正文中";
  const safeDescription = description || "根据正文内容补充配图。";
  return `![图片占位：${safeTitle}]() ${safePlacement}：${safeDescription}`;
}

export function articleToWechatHtml(article, options = {}) {
  return articleToWechatPublishHtml(article, options);
}

export function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
